import {useTranslation} from "react-i18next";
import CRUDTable from "../CRUDTable";
import {filialDivisionCRUD, protocolCRUD} from "../../../http";
import React from "react";
import {getProtocolForms} from "../../../redux/actions/protocolFormActions";
import {getDepartments, getDivisions, getFilials} from "../../../redux/actions";
import {getProtocolStates} from "../../../redux/actions/protocolStateActions";
import { trimName } from "../../../functions/stringHelpers";
import { requestStateIds } from "../../../models/requestStateIds";
import { protocolStateIds } from "../../../models/protocolStateIds";
import { asJSONSafe } from "../../../functions";
import ProtocolReceiversSection from "../../../sections/ProtocolReceiversSection";
import { getOrderTypeLabel, orderTypeIds } from "../../../models/orderTypeIds";
import {
    getComplianceStatusLabel,
    getConfirmationStatusLabel,
    normalizeComplianceStatus,
    shouldShowProtocolCompliance,
} from "../../../models/protocolCompliance";

const normalizeFilialDivisionRowForFilter = (row) => ({
    ...row,
    id: row.id ?? row.Id,
    name: row.name ?? row.Name,
    filialId: row.filialId ?? row.FilialId,
});

const resolveRowPurpose = (row) =>
    row?.objectReferral?.purposeOfSelection ||
    row?.objectReferral?.PurposeOfSelection ||
    row?.ObjectReferral?.purposeOfSelection ||
    row?.ObjectReferral?.PurposeOfSelection ||
    row?.purposeOfSelection ||
    null;

const rowShowsCompliance = (row) => {
    // Уже заполненный статус (в т.ч. «к подтверждению») показываем всегда
    if (normalizeComplianceStatus(row?.complianceStatus ?? row?.ComplianceStatus) != null) {
        return true;
    }
    return shouldShowProtocolCompliance({
        orderType: row?.orderType ?? orderTypeIds.government,
        purpose: resolveRowPurpose(row),
    });
};

export default function ProtocolCRUDTable({
    extraQuery: extraQueryProp,
    /** Колонки соответствия — только госзаказ / «к подтверждению», не в платных списках */
    showComplianceColumns = false,
    ...props
}) {
    const {t} = useTranslation();

    const columns = [
        {title: t("protocolList.tableItem.id"), dataIndex: "id", key: "id"},
        {title: t("protocolList.tableItem.number"), dataIndex: "number", key: "number"},
        {
            title: t("protocolList.tableItem.orderType"),
            dataIndex: "orderType",
            key: "orderType",
            render: (unknown, row) => getOrderTypeLabel(row.orderType),
        },
        {
            title: t("protocolList.tableItem.state"),
            dataIndex: "state",
            key: "state",
            render: (unknown, row) => ({Created: 'Создан', Signed: 'Подписан', Declined: "Отклонён"}[row.state?.code])
        },
        ...(showComplianceColumns
            ? [
        {
            title: "Статус соответствия",
            dataIndex: "complianceStatus",
            key: "complianceStatus",
            render: (unknown, row) => {
                if (!rowShowsCompliance(row)) {
                    return <span style={{color: "gray"}}>—</span>;
                }
                const label = getComplianceStatusLabel(
                    row.complianceStatus ?? row.ComplianceStatus
                );
                return label || <span style={{color: "gray"}}>—</span>;
            }
        },
        {
            title: "Подтверждение",
            dataIndex: "confirmationStatus",
            key: "confirmationStatus",
            render: (unknown, row) => {
                if (!rowShowsCompliance(row)) {
                    return <span style={{color: "gray"}}>—</span>;
                }
                const label = getConfirmationStatusLabel(
                    row.confirmationStatus ?? row.ConfirmationStatus
                );
                return label || <span style={{color: "gray"}}>—</span>;
            }
        },
            ]
            : []),
        {
            title: t("protocolList.tableItem.createdAt"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (unknown, row) => row.createdAt && Intl.DateTimeFormat('ru-RU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric'
            }).format(new Date(row.createdAt))
        },
        {
            title: t("protocolList.tableItem.form"),
            dataIndex: "form",
            key: "form",
            render: (unknown, row) => row.form ? (row.form.number.length == 2 ? `0${row.form.number}` : row.form.number) : <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: t("protocolList.tableItem.department"),
            dataIndex: "department",
            key: "department",
            render: (unknown, row) => row.department ? row.department.name :
                <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: t("protocolList.tableItem.division"),
            dataIndex: "division",
            key: "division",
            render: (unknown, row) => row.division ? row.division.name : <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: t("protocolList.tableItem.signers"),
            dataIndex: "signers",
            key: "signers",
            render: (unknown, row) => {
                let color = '#aa0000';
                const count = row.signers.length;
                const signedCount = row.signers.filter(_ => _.isSigned).length;
                const delta = count - signedCount;
                switch (delta) {
                    case 0:
                        color = '#00aa00';
                        break;
                    case 1:
                        color = '#aaaa00';
                        break;
                }

                return <div>
                    {row.signers.map(_ => <div style={{backgroundColor: color}}><span style={{fontWeight: _.isSigned ? 'bold' : 'normal'}}>{trimName(_.employee?.fullName)}</span></div>)}
                </div>
            }
        },
        {
            title: t("protocolList.tableItem.performer"),
            dataIndex: "performer",
            key: "performer",
            render: (unknown, row) => row.performer ? row.performer?.fullName :
                <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: t("protocolList.tableItem.responsible"),
            dataIndex: "responsible",
            key: "responsible",
            render: (unknown, row) => row.responsible ? row.responsible?.fullName :
                <span style={{color: "gray"}}>Не задан</span>
        },
        {
            dataIndex: "filial",
            key: "filial",
            title: "Филиал",
            render: (unknown, row) => {
                return row.filial?.name
            }
        },
        {
            dataIndex: "filialDivision",
            key: "filialDivision",
            title: "Районное отделение",
            render: (unknown, row) =>
                row.filialDivisionId
                    ? row.filialDivision?.name ||
                      row.filialDivision?.Name ||
                      `ID: ${row.filialDivisionId}`
                    : "",
        },
        {
            dataIndex: "receivers",
            key: "receivers",
            title: "Доставка во внешние системы",
            width: 520,
            render: (value, row) => {
                return <ProtocolReceiversSection data={value} />
            }
        },
    ]

    const filterFields = [
        {
            title: "Номер протокола",
            key: "formId",
            compareType: "equals",
            reducerKey: "protocolForm",
            reducerArrayKey: "protocolForms",
            reducerAction: getProtocolForms,
            operandPosition: 1,
            optionLabelKey: "number",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Дата создания от",
            key: "createdAt",
            operandPosition: 1,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
        {
            title: "Дата создания до",
            key: "createdAt",
            operandPosition: 2,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
        {
            title: "Филиал",
            key: "filialId",
            compareType: "equals",
            reducerKey: "filial",
            reducerArrayKey: "filials",
            reducerAction: getFilials,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Районное отделение",
            key: "filialDivisionId",
            compareType: "equals",
            reducerKey: "filialDivision",
            operandPosition: 1,
            parentKey: 'filialId',
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
            useDomainSelect2: true,
            domainSelectCrud: filialDivisionCRUD,
            selectMembers: ["Id", "Name", "FilialId"],
            normalizeOption: normalizeFilialDivisionRowForFilter,
            domainSelect2SkipFetchWithoutParent: true,
            domainSelect2NoParentHint: "Выберите филиал",
        },
        {
            title: "Лаборатория",
            key: "departmentId",
            compareType: "equals",
            reducerKey: "department",
            reducerArrayKey: "departments",
            reducerAction: getDepartments,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Отдел",
            key: "divisionId",
            compareType: "equals",
            reducerKey: "division",
            reducerArrayKey: "divisions",
            reducerAction: getDivisions,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Статус",
            key: "stateId",
            compareType: "equals",
            reducerKey: "protocolState",
            reducerArrayKey: "protocolStates",
            reducerAction: getProtocolStates,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
    ];

    const searchFields = [
        {
            title: "Номер",
            key: "number",
            compareType: "equals",
            operandPosition: 1,
            filterType: "normal",
        }]

    const mergedExtraQuery = {
        ...(extraQueryProp || {}),
        includes: [
            ...new Set([
                "FilialDivision",
                ...(showComplianceColumns ? ["ObjectReferral", "ObjectReferral.PurposeOfSelection"] : []),
                ...((extraQueryProp && extraQueryProp.includes) || []),
            ]),
        ],
    };

    return (
        <>
            <CRUDTable
                search={protocolCRUD.search}
                searchFields={searchFields}
                scroll={{ x: 1900 }}
                columns={columns.filter(({filial, filialDivision}) => {
                    const employee = window.localStorage.employee ? JSON.parse(window.localStorage.employee) : null;
                    if (filial?.id && employee?.filialId) {
                        if (filial.id !== employee.filialId) return false
                    }
                    if (filialDivision?.id && employee?.filialDivisionId) {
                        if (filialDivision?.id !== employee?.filialDivisionId) return false
                    }
                    return true
                })}
                filterFields={filterFields}
                rowClassNameHandler={(record) => {
                    if (record.state?.code === "Signed") {
                        return 'rowSigned'
                    }
                    if (record.stateId === protocolStateIds.declined) {
                        return 'banned'
                    }
                }}
                {...props}
                extraQuery={mergedExtraQuery}
            />
        </>
    )
}
